import React from 'react';
import { AlertTriangleIcon, LockIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Card } from '../ui/Card';
import type { Lead, LeadSource, PriorityRule } from '../../types';
import { formatAge, requiredCallsFor } from '../../utils/format';
interface QueuePanelProps {
  queue: Lead[];
  currentLeadId?: string;
  sources: LeadSource[];
  priority: PriorityRule[];
  className?: string;
}
function hasOverdue(lead: Lead): boolean {
  return lead.activities.some(a => a.dueInMinutes !== undefined && a.dueInMinutes < 0);
}
export function QueuePanel({
  queue,
  currentLeadId,
  sources,
  priority,
  className
}: QueuePanelProps) {
  const enabled = priority.filter(r => r.enabled);
  const upcoming = queue.slice(0, 10);
  return <Card as="aside" className={twMerge('flex flex-col overflow-hidden', className)} data-id="element-255" data-mp="3nbdnu83ng1fu">
      <div className="border-b border-ink-200 px-5 py-4" data-id="element-256" data-mp="1hhdr3i50g8ml">
        <div className="flex items-center justify-between" data-id="element-257" data-mp="1lih0blsjz6eu">
          <h2 className="text-sm font-semibold text-ink-900" data-id="element-258" data-mp="1sequ94ti80qv">Up Next</h2>
          <span className="tabular text-xs font-medium text-ink-500" data-id="element-259" data-mp="1scxn7oezbeic">{queue.length} in queue</span>
        </div>
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-500" data-id="element-260" data-mp="1ndkzl1gaa3ep">
          <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-261" data-mp="2240iuap531a8" />
          Sequence set by management — view only
        </p>
      </div>

      <ol className="divide-y divide-ink-200 overflow-y-auto" data-id="element-262" data-mp="19fwf3uq4oya6">
        {upcoming.map((lead, index) => {
        const source = sources.find(s => s.id === lead.sourceId);
        const required = requiredCallsFor(source, lead.ageMinutes);
        const remainingCalls = Math.max(0, required - lead.callsToday);
        const priorityIndex = enabled.findIndex(r => r.id === lead.priorityRuleId) + 1;
        const isCurrent = lead.id === currentLeadId;
        return <li key={lead.id} className={twMerge('flex gap-3 px-5 py-3.5 transition-colors', isCurrent ? 'bg-brand-50/60' : 'hover:bg-ink-50')} data-id="element-263" data-mp="2uvbumi8du28f" data-mpi={index}>
              <span className={twMerge('tabular mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold', isCurrent ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-600')} data-id="element-264" data-mp="31vw7te04if1q">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1" data-id="element-265" data-mp="2e8o3ayaira0r">
                <div className="flex items-center gap-2" data-id="element-266" data-mp="2j5eiljei21l4">
                  <p className="truncate text-sm font-medium text-ink-900" data-id="element-267" data-mp="2j3tp7x3x8oou">{lead.name}</p>
                  {hasOverdue(lead) ? <AlertTriangleIcon className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-label="Has overdue activity" data-id="element-268" data-mp="2svhcdltg3jki" /> : null}
                </div>
                <p className="mt-0.5 truncate text-xs text-ink-500" data-id="element-269" data-mp="20e59vccjzgul">
                  {source?.name} · {formatAge(lead.ageMinutes)}
                </p>
                <div className="mt-1.5 flex items-center gap-2 text-[11px] font-medium" data-id="element-270" data-mp="diql02i9kmm9">
                  <span className="rounded-full bg-ink-100 px-1.5 py-0.5 text-ink-600" data-id="element-271" data-mp="dgw20tqjz4kt">
                    P{priorityIndex || '—'}
                  </span>
                  <span className="tabular text-ink-500" data-id="element-272" data-mp="3jrccjxi0qz9">
                    {remainingCalls} call{remainingCalls === 1 ? '' : 's'} left today
                  </span>
                </div>
              </div>
            </li>;
      })}
        {upcoming.length === 0 ? <li className="px-5 py-8 text-center text-sm text-ink-500" data-id="element-273" data-mp="3lxqv5hhjtalp">
            Queue is clear. Great work today.
          </li> : null}
      </ol>
    </Card>;
}